/**
 * Amplitude Analytics and Session Replay SDK initialization module.
 *
 * Analytics SDK init is DEFERRED to post-load idle — the Amplitude
 * browser SDK buffers track/identify calls made before `init()` and flushes
 * them afterwards, so deferring costs no events while keeping SDK setup (and
 * the web session-replay plugin) off the boot→interactive critical path. The
 * SDK is accessed via direct imports from @amplitude/analytics-browser; no
 * React context provider is needed as it manages its own global state.
 *
 * Feature flags do NOT come from here. They are served by Flagsmith via
 * OpenFeature (see src/features/flags), with PostHog as the legacy path;
 * Amplitude's role in experimentation is analysis only, via the `$exposure`
 * events that `useManualExposure` emits on the Analytics SDK.
 *
 * Session Replay captures user sessions for playback in Amplitude (web only —
 * desktop uses Datadog replay).
 */

import { workspaceContextEnrichmentPlugin } from '@/providers/amplitude-workspace-context-plugin';
import { isTauri } from '@/tauri/utils';
import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import { deferTelemetryInit } from '@/utils/deferredTelemetry';
import { logger } from '@/utils/logger/logger';
import * as amplitude from '@amplitude/analytics-browser';
import { Types } from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { registerAnalyticsAdapter } from '@heidi/runtime/analytics';
import { sanitizeError } from '@heidi/runtime/logger';

// P2 [3/5]: Amplitude transport binding for the @heidi/runtime/analytics
// pipeline — registered at this SDK-owning site, gated like the F1 init gate
// so a suppressed build registers nothing (F5: absence). Pre-init sends queue
// on the SDK's internal buffer exactly as the legacy effect's unconditional
// `amplitude.track` calls did.
if (typeof window !== 'undefined' && isSaasTelemetryEnabled) {
  registerAnalyticsAdapter({
    destination: 'amplitude',
    enabled: true,
    send: (payload) => {
      amplitude.track(payload.event, payload.props);
    },
  });
}

let amplitudeInitialized = false;

export const initAmplitude = (): void => {
  if (amplitudeInitialized || typeof window === 'undefined') {
    return;
  }

  // TELEMETRY-GATE: F1 function-entry gate. Canonical pattern referenced from
  // .ai-context/onprem-guardrails.md — deferred-init flow short-circuits when
  // telemetry is off so the SDK never reaches the reverse-proxy at
  // sys.heidihealth.com.
  if (!isSaasTelemetryEnabled) {
    return;
  }

  // Skip initialization in E2E test environments to prevent external CDN requests
  if (typeof window !== 'undefined' && window.__IS_E2E__) {
    return;
  }

  const apiKey = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY;

  if (!apiKey) {
    return;
  }

  amplitudeInitialized = true;

  // APP-6396: Session Replay is web-only. On the desktop (Tauri) build, Datadog
  // RUM session replay is the single recorder (see src/utils/tracing.ts) —
  // running two DOM-serializing replay recorders in the OS webview is a major
  // perf cost and the top cause of interaction jank on desktop. Amplitude
  // analytics events below still fire on desktop; only replay is gated off.
  if (!isTauri()) {
    // Add Session Replay plugin before init (recommended by Amplitude)
    // Sample rate is controlled via Amplitude remote config (dashboard settings)
    // Do NOT set local sampleRate - it overrides remote config
    const isLocalDev =
      getEnvironment() === ENV_NAME.DEV || getEnvironment() === ENV_NAME.LOCALHOST;

    const sessionReplay = sessionReplayPlugin({
      // Reverse proxy URL for Session Replay data (CloudFront -> api-sr.eu.amplitude.com)
      trackServerUrl: 'https://sys.heidihealth.com/sessions/v2/track',
      // Reverse proxy URL for Session Replay config (CloudFront -> sr-client-cfg.eu.amplitude.com)
      // SDK appends /{apiKey}?config_group=browser
      configServerUrl: 'https://sys.heidihealth.com/config',
      debugMode: isLocalDev,
    });

    amplitude.add(sessionReplay).promise.catch((err) => {
      // sanitizeError keeps status/code and drops the raw message: third-party
      // error text must never reach Datadog (INC-345, telemetry PHI/PII rule).
      logger.error('[Amplitude Session Replay] Failed to register plugin:', {
        error: sanitizeError(err),
      });
    });
  }

  // Stamp workspace_id + team_id onto every Amplitude event (web AND desktop, so
  // NOT inside the web-only session-replay block above). Added before init() so
  // it enriches events the SDK buffered pre-init too. Twin of the Snowplow
  // workspace_context generator — see amplitude-workspace-context-plugin.ts.
  amplitude.add(workspaceContextEnrichmentPlugin()).promise.catch((err) => {
    logger.error('[Amplitude Workspace Context] Failed to register plugin:', {
      error: sanitizeError(err),
    });
  });

  amplitude.init(apiKey, {
    // The packaged macOS webview serves the app from tauri://localhost, and
    // WKWebView drops document.cookie writes on a custom scheme. The SDK's
    // default cookie identity storage therefore never persists there: every
    // page load mints a new device_id, which splits one desktop user into
    // several Amplitude users (e.g. across the macOS permissions "Quit &
    // Reopen") and breaks onboarding funnels. localStorage persists and is
    // shared by every webview window on the same origin.
    //
    // Gated on the scheme rather than isTauri() because useHttpsScheme only
    // applies to Windows/Android: Windows packages as https://tauri.localhost,
    // where cookies already work. Switching storage there would strand the
    // existing AMP_* cookie identity — the SDK reads only the selected store
    // and does not migrate a current-format cookie — minting a new device_id
    // for every installed Windows user on upgrade. The macOS dev server
    // (http://localhost:3010) keeps cookies for the same reason.
    ...(window.location.protocol === 'tauri:' && {
      identityStorage: 'localStorage' as const,
    }),
    serverUrl: 'https://sys.heidihealth.com/2/httpapi',
    remoteConfig: {
      serverUrl: 'https://sys.heidihealth.com/config',
    },
    defaultTracking: {
      sessions: true,
      pageViews: false,
      formInteractions: false,
      fileDownloads: false,
      attribution: true,
    },
    logLevel:
      process.env.NEXT_PUBLIC_AMPLITUDE_LOG_LEVEL === 'debug'
        ? Types.LogLevel.Debug
        : Types.LogLevel.None,
  });
  // Device/build dimensions (App, app_type, client_platform, platform,
  // installer_type, distribution_channel) are intentionally NOT set as Amplitude
  // user properties here: they are not user identity, and as last-write-wins user
  // props they get clobbered across a user's web/desktop sessions. They ride as
  // per-event properties via trackEventV2 instead (see analyticsScribeModel).
};

/**
 * Reset Amplitude identity and clear stored user data.
 * Call this during logout to prevent cross-user contamination.
 */
export const resetAmplitude = (): void => {
  try {
    amplitude.reset();
  } catch (error) {
    logger.error('[Amplitude] Failed to reset:', { error });
  }
};

// Schedule init on module load in browser environments. Deferred to post-load
// idle: the Amplitude SDK buffers track/identify calls made before `init()`
// and flushes them once it runs, so nothing is lost in the deferral window.
if (typeof window !== 'undefined') {
  deferTelemetryInit(() => {
    initAmplitude();
  });
}
